import { Ticket } from 'features/Orders/types';

/**
 * Counts tickets per ticket-type name for a single order/grant.
 *
 * The per-ticket `tickets` array (one entry per ticket, each carrying
 * `ticket_type_name`) is the source of truth for quantity. The backend's
 * `tickets_name` is a DEDUPLICATED set (built from a Go `map[string]bool`), so
 * it holds one entry per *distinct* type — counting it under-reports quantity
 * (the "shows 1" bug). We fall back to `tickets_name` only for legacy records
 * that predate the per-ticket payload (empty `tickets`), where each distinct
 * name contributes 1 — best effort, since the true per-type quantity is
 * unknowable for those rows.
 *
 * Shared by the Comped Tickets table, the Transactions ticket-type breakdown,
 * and the Analytics inventory comped donut, so all three count the same way and
 * all show count-only — money lives in the Transactions Amount column, not here
 * (see docs/transactions_business_logic.md §3).
 */
export const countTicketsByType = (
	tickets: Ticket[] | undefined,
	ticketsName: string[] | undefined
): Record<string, number> => {
	const counts: Record<string, number> = {};
	if (tickets && tickets.length > 0) {
		for (const tk of tickets) {
			counts[tk.ticket_type_name] = (counts[tk.ticket_type_name] ?? 0) + 1;
		}
		return counts;
	}
	for (const name of ticketsName ?? []) {
		counts[name] = (counts[name] ?? 0) + 1;
	}
	return counts;
};
